import { type Store, createLens } from '@frontend/viewer-core';

import type {
  DxfSessionState,
  SelectionGranularity,
  SelectionGranularityManager,
} from './types';

export const createSelectionGranularitySlice = (
  state: Store<DxfSessionState>,
): Store<SelectionGranularity> =>
  createLens(
    state,
    (current) => current.selectionGranularity,
    (current, selectionGranularity) => ({ ...current, selectionGranularity }),
  );

/**
 * The other granularity.
 *
 * Its own function rather than a ternary at each call site because the Alt
 * modifier — which inverts the active granularity for as long as it is held —
 * is defined in terms of it, and that inversion should be one rule rather than
 * one per gesture that consults it.
 */
export const oppositeGranularity = (
  granularity: SelectionGranularity,
): SelectionGranularity => (granularity === 'loop' ? 'edge' : 'loop');

/**
 * Selection-mode shortcuts, keyed by `KeyboardEvent.code` — physical key
 * position rather than the character, so the binding survives a non-US layout,
 * exactly as `BRUSH_HOTKEY_ENTRIES` does.
 *
 * V and A are Illustrator's and Figma's: V for the whole-object selector, A for
 * the direct one that reaches inside it. Users bring those fingers with them,
 * and neither collides with the role brushes (C/E/B/X).
 *
 * Unlike the brush keys these are *not* mapping-only — selection modes exist
 * while viewing too, so the input controller gates them on neither mode.
 */
export const GRANULARITY_HOTKEY_ENTRIES: readonly (readonly [
  string,
  SelectionGranularity,
])[] = [
  ['KeyV', 'loop'],
  ['KeyA', 'edge'],
] as const;

/** `KeyboardEvent.code` → granularity, for the input controller. */
export const GRANULARITY_HOTKEY_BY_CODE: ReadonlyMap<
  string,
  SelectionGranularity
> = new Map(GRANULARITY_HOTKEY_ENTRIES);

/** Granularity → single-letter label (`KeyV` → `V`), for tooltips and help. */
export const granularityHotkeyLabel = (
  granularity: SelectionGranularity,
): string => {
  const entry = GRANULARITY_HOTKEY_ENTRIES.find(
    ([, mapped]) => mapped === granularity,
  );
  return entry ? entry[0].replace(/^Key/, '') : '';
};

/**
 * Loop versus edge selection, and the Alt modifier that inverts it.
 *
 * An axis of its own rather than a `ToolId`, for the reason `mode` is: switching
 * tools clears the selection, and switching selection mode must not.
 *
 * The inversion is held here rather than in session state, deliberately. It is
 * not a mode the user is in — it lasts exactly as long as a key is down, and no
 * React consumer should re-render on it. What *does* have to react is the hover
 * accent, and that already re-renders because pressing Alt makes the input
 * controller re-pick under the pointer and publish a new `hovered`; the accent
 * follows that, not this flag. Keeping it off `DxfSessionState` also keeps it out
 * of the mapping undo snapshot, where a transient key press has no business.
 *
 * Everything that picks reads `effective()` rather than the store, so no caller
 * learns that the granularity it is given can be overridden.
 */
export const createSelectionGranularityController = ({
  granularity,
}: {
  granularity: Store<SelectionGranularity>;
}): SelectionGranularityManager => {
  let isInverted = false;

  return {
    getState: granularity.getState,
    subscribe: granularity.subscribe,
    set(next) {
      granularity.setState((previous) => (previous === next ? previous : next));
    },

    effective: () =>
      isInverted
        ? oppositeGranularity(granularity.getState())
        : granularity.getState(),

    setInverted(next) {
      isInverted = next;
    },

    isInverted: () => isInverted,
  };
};
